import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import {
  DemandPlanVariant,
  DemandPlanVariantStatus,
} from "@models/DemandPlanVariant";

import { EditingContext } from "./EditingContext";

export const EditingProvider = ({ children, data, isFetching }) => {
  const [editing, setEditing] = useState<DemandPlanVariant | null>(null);

  const editor = useMemo(() => {
    return {
      current: editing,
      next() {
        this.moveEditIndex(1);
      },
      previous() {
        this.moveEditIndex(-1);
      },
      moveEditIndex(offset) {
        if (!editing) {
          return;
        }

        const cellIndex = data.findIndex((r) => editing.id === r.id);
        if (!~cellIndex) {
          return;
        }

        let nextRow: undefined | DemandPlanVariant = data[cellIndex + offset];
        let stepper = offset;

        while (nextRow && !this.canEdit(nextRow)) {
          stepper = stepper + offset;
          nextRow = data[cellIndex + stepper];
        }

        if (nextRow) {
          setEditing(nextRow);
        } else {
          setEditing(null);
        }
      },
      start(rec) {
        if (this.canEdit(rec)) {
          setEditing(rec);
        }
      },
      complete: (rec) => {
        rec.id === editing?.id && setEditing(null);
      },
      canEdit: (rec) => {
        return (
          rec.status === DemandPlanVariantStatus.Terminated ||
          rec.status === DemandPlanVariantStatus.Draft ||
          rec.status === DemandPlanVariantStatus.InProgress ||
          rec.status === DemandPlanVariantStatus.PendingReview ||
          rec.status === DemandPlanVariantStatus.ReadyForPurchase
        );
      },
    };
  }, [data, editing, setEditing]);

  const formMethods = useForm<DemandPlanVariant>({
    defaultValues: {},
  });

  useEffect(() => {
    if (isFetching) return;
    const formValues = data.reduce((cur, rec) => {
      cur[`toOrder-${rec.id}`] = rec.toOrder ?? rec.recommendedToOrder;
      return cur;
    }, {});

    formMethods.reset(formValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, data]);

  useEffect(() => {
    const keyNav = (e) => {
      if (e.target.name?.startsWith("toOrder")) {
        if (e.key === "Enter" || e.key === "ArrowUp" || e.key === "ArrowDown") {
          e.preventDefault();
          if (e.key === "ArrowUp") {
            editor.previous();
          } else {
            editor.next();
          }

          document.removeEventListener("keydown", keyNav);
        }
        if (e.key === "Tab") {
          document.removeEventListener("keydown", keyNav);
        }
      }
    };
    document.addEventListener("keydown", keyNav);
    return () => {
      document.removeEventListener("keydown", keyNav);
    };
  }, [editor]);

  return (
    <EditingContext.Provider value={editor}>
      <FormProvider {...formMethods}>{children}</FormProvider>
    </EditingContext.Provider>
  );
};
